import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, Gift, Link2, RefreshCw, Users } from 'lucide-react';
import { userApi, type AccountInvitations } from '../lib/api';
import { useI18n } from '../i18n';

export function InvitationPanel({ onClaim }: { onClaim: () => Promise<void> }) {
  const { t, locale } = useI18n();
  const [data, setData] = useState<AccountInvitations | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [copied, setCopied] = useState('');
  const shareRef = useRef<HTMLInputElement>(null);
  const deliveryCopy:Record<string,string>={unbound:'尚未绑定加速节点，奖励会保留，请联系支持开通后领取。',not_enabled:'节点权益控制尚未启用，奖励会保留，启用后即可领取。',pending:'权益正在同步到节点，通常在 10 分钟内更新。',confirmed:'节点已确认额度与有效期。',blocked:'节点同步遇到问题，奖励会保留，请联系支持。',stale:'节点暂时离线，奖励会保留，恢复后再领取。'};
  const canClaim=!data?.delivery || ['confirmed','pending'].includes(data.delivery.status);
  deliveryCopy.provisioning='账户正在自动开通，奖励会保留，节点就绪后即可领取。';
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await userApi.invitations()); }
    catch (e) { setError(e instanceof Error ? e.message : '邀请服务暂不可用，请重试。'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const date = (value: number) => new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(value);
  const copy = async (value: string, key: string) => {
    try { await navigator.clipboard.writeText(value); setCopied(key); setNotice(t('已复制，分享给好友即可。')); }
    catch { shareRef.current?.select(); setNotice(t('无法访问剪贴板，请选中并复制邀请码或链接。')); }
  };
  const claim = async (id: string) => {
    setClaiming(id); setError(''); setNotice('');
    try {
      await userApi.claimInvitation(id);
      setNotice(t('已领取权益，节点确认后可用，请刷新订阅。'));
      await load(); await onClaim();
    } catch (e) { setError(e instanceof Error ? e.message : '奖励领取失败，请重试。'); }
    finally { setClaiming(null); }
  };
  return <section className="invitation-panel" aria-label={t('邀请免费加速')}>
    <header className="invitation-hero">
      <div className="invitation-hero-copy">
        <span className="invitation-eyebrow"><Gift aria-hidden="true" />{t('邀请免费加速')}</span>
        <h2>{t('把云流分享给朋友，')}<br />{t('下一段加速，我们请。')}</h2>
        <p>{t('每邀请一位新好友完成注册，即可领取一份会员奖励。')}</p>
      </div>
      <div className="invitation-reward" aria-label={t('每位好友注册奖励')}>
        <span className="invitation-reward-caption">{t('每位好友注册奖励')}</span>
        <div className="invitation-reward-values">
          <span><strong>24</strong><small>{t('小时会员')}</small></span>
          <i aria-hidden="true">+</i>
          <span><strong>3</strong><small>GB {t('高速流量')}</small></span>
        </div>
      </div>
    </header>
    {error && <p className="account-error" role="alert">{t(error)}</p>}
    {notice && <p className="invitation-notice" role="status">{notice}</p>}
    {loading && !data ? <p role="status">{t('正在加载邀请记录…')}</p> : data ? <>
      <div className="invitation-sharing">
        <div className="invitation-code-block">
          <span>{t('我的邀请码')}</span>
          <div className="invitation-code-row">
            <strong className="invitation-code">{data.code}</strong>
            <button onClick={() => void copy(data.code, 'code')} type="button" className="account-secondary-button">{copied === 'code' ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}{t('复制邀请码')}</button>
          </div>
        </div>
        <div className="invitation-link-block">
          <label htmlFor="invite-share-link">{t('专属邀请链接')}</label>
          <div className="invitation-link-row">
            <input id="invite-share-link" ref={shareRef} readOnly value={data.shareUrl} onFocus={e => e.target.select()} />
            <button onClick={() => void copy(data.shareUrl, 'link')} type="button" className="account-primary-button">{copied === 'link' ? <Check aria-hidden="true" /> : <Link2 aria-hidden="true" />}{t('复制邀请链接')}</button>
          </div>
          <small>{t('好友打开链接后，邀请码会保存 30 天，注册时自动填入。')}</small>
        </div>
      </div>
      <div className="invitation-stats">
        <div><span className="invitation-stat-label"><Users aria-hidden="true" />{t('有效邀请')}</span><strong>{data.invitedCount}</strong></div>
        <div><span className="invitation-stat-label"><Gift aria-hidden="true" />{t('待领取奖励')}</span><strong>{data.pendingCount}</strong></div>
        <div><span className="invitation-stat-label"><span className="invitation-stat-clock">h</span>{t('已领取会员时长')}</span><strong>{data.claimedHours}<small>h</small></strong></div>
      </div>
      {data.delivery && <p className="invitation-notice" role="status">{t(deliveryCopy[data.delivery.status]??deliveryCopy.pending)} {t('会员速度上限尚未在节点验证，不作为已生效承诺。')}</p>}
      <section className="invitation-history"><header><h3>{t('邀请奖励记录')}</h3><button className="account-subtle-button" type="button" disabled={loading || claiming !== null} onClick={() => void load()}><RefreshCw />{t('刷新记录')}</button></header>
        {data.history.length === 0 ? <p className="invitation-empty">{t('还没有邀请记录。复制链接，邀请第一位朋友吧。')}</p> : <ul>{data.history.map((reward) => <li key={reward.id}><span className="invitation-history-icon"><Gift /></span><div><strong>{t('好友注册奖励')}</strong><small>{date(reward.createdAt)} · 24h + 3GB</small>{reward.startsAt && reward.expiresAt && <small>{t('生效时间')} {date(reward.startsAt)} — {date(reward.expiresAt)}</small>}</div>{reward.status === 'earned' ? <button className="account-primary-button" disabled={claiming !== null || !canClaim || loading} onClick={() => void claim(reward.id)}>{t(claiming === reward.id ? '领取中…' : '领取奖励')}</button> : <span className="invitation-status">{t(reward.status === 'claimed' ? '已领取' : '不可领取')}</span>}</li>)}</ul>}
      </section>
    </> : <button type="button" onClick={() => void load()}>{t('重试')}</button>}
    <details className="invitation-rules"><summary>{t('奖励规则')}</summary><ul><li>{t('奖励发给邀请人，每位新好友仅计一次；邀请码需在注册时填写，注册后不能补填或更换。')}</li><li>{t('奖励领取后生效；已有会员按最后一个会员周期接续，3GB 随对应的 24 小时奖励周期到期。')}</li><li>{t('同一设备仅允许注册一个新账户，同网络每天最多注册 5 个；重复注册、自己邀请自己不能获得奖励。')}</li><li>{t('本页为会员权益账本；节点实时流量与连接状态以订阅管理为准。')}</li></ul></details>
  </section>;
}
